import {
  BookingServiceClient,
  IInstructorResponse,
  UserServiceClient,
  WaitingListServiceClient,
} from 'fitbeat.api-clients';
import { ContentType, IPost, UserWorkoutSession } from 'fitbeat.gym-core';
import { UserProfile } from 'fitbeat.gym-core';
import { IUserLeaderboardViewModel, NextWorkout } from 'fitbeat.models';
import moment from 'moment-timezone';
import { Platform } from 'react-native';
import FastImage from 'react-native-fast-image';
import { IAppStore } from '../HomeWorkouts';
import { getInstructorListSuccess } from '../HomeWorkouts/actions';
import { analyicsForAction } from '../services/google-analytics/analyicsForAction';
import { loadLeaderboardsForUser } from '../Tracking/actions';
import { closeAllModals } from '../ui/redux/uiActions';
import { Logger } from '../utils/Logger';
import { addDeepLinkParams } from '../utils/notifications';
import {
  ALL_DASHBOARD_BLOG_HELP_FAILURE,
  ALL_DASHBOARD_BLOG_HELP_REQUEST,
  ALL_DASHBOARD_BLOG_HELP_SUCCESS,
  ALL_DASHBOARD_BLOG_INSIGHTS_REQUEST,
  ALL_DASHBOARD_BLOG_INSIGHTS_SUCCESS,
  BOOKING_DASH_TILE_SELECTED,
  BOOKING_FROM_DASHBOARD, CLOSE_LEADERBOARD_MODAL,
  DASHBOARD_BLOG_CARD_PRESSED,
  DASHBOARD_BLOG_INSIGHTS_FAILURE,
  DASHBOARD_BLOG_INSIGHTS_REQUEST,
  DASHBOARD_BLOG_INSIGHTS_SUCCESS,
  DASHBOARD_OPENED_FAILURE,
  DASHBOARD_OPENED_REQUEST,
  DASHBOARD_OPENED_SUCCESS,
  JOIN_LEADERBOARD_FAILURE,
  JOIN_LEADERBOARD_REQUEST,
  JOIN_LEADERBOARD_SUCCESS,
  NEXT_BOOKING_DASH_TILE_SELECTED,
  NUTRITION_DASH_TILE_SELECTED,
  OPEN_LEADERBOARD_MODAL, SEE_ALL_LEADERBOARDS,
  SELECT_TEAM,
  SHOW_TEAM_SELECT_SCREEN,
  USER_GOAL_CHANGED,
  WEEKLY_GOAL_DATA_FAILURE,
  WEEKLY_GOAL_DATA_REQUEST,
  WEEKLY_GOAL_DATA_SUCCESS,
  WORKOUT_TILE_DATA_FAILURE,
  WORKOUT_TILE_DATA_REQUEST,
  WORKOUT_TILE_DATA_SUCCESS,
} from './actionTypes';

const isWeb = Platform.OS === 'web';
// TRACKING DATA

export const startBookingFromDashboard = () => {
  return {
    type: BOOKING_FROM_DASHBOARD,
    ...analyicsForAction(BOOKING_FROM_DASHBOARD),
  };
};

export const bookingDashTileSelected = () => {
  return {
    type: BOOKING_DASH_TILE_SELECTED,
    ...analyicsForAction(BOOKING_DASH_TILE_SELECTED),
  };
};

export const nextBookingDashTileSelected = () => {
  return {
    type: NEXT_BOOKING_DASH_TILE_SELECTED,
    ...analyicsForAction(NEXT_BOOKING_DASH_TILE_SELECTED),
  };
};

export const nutritionDashTileSelected = () => {
  return {
    type: NUTRITION_DASH_TILE_SELECTED,
    ...analyicsForAction(NUTRITION_DASH_TILE_SELECTED),
  };
};

export const userGoalChanged = () => {
  return {
    type: USER_GOAL_CHANGED,
  };
};

// NEXT WORKOUT DATA
const getWorkoutTileDataRequest = (hideLoader: boolean) => {
  return {
    type: WORKOUT_TILE_DATA_REQUEST,
    payload: {
      hideLoader,
    },
  };
};

const getWorkoutTileDataSuccess = (workoutData: NextWorkout) => {
  return {
    type: WORKOUT_TILE_DATA_SUCCESS,
    payload: {
      workoutData,
    },
  };
};

const getWorkoutTileDataFailed = (error: string) => {
  return {
    type: WORKOUT_TILE_DATA_FAILURE,
    payload: {
      error,
    },
  };
};

export const getWorkoutTileData = (userServiceClient: any, hideLoader: boolean) => {
  return async (dispatch) => {
    Logger.leaveBreadcrumb('loading next workout data');
    dispatch(getWorkoutTileDataRequest(hideLoader));
    try {
      const today = new Date();
      const datestamp = today.valueOf();
      const data = await userServiceClient.getWorkoutTileInfo(datestamp);
      dispatch(getWorkoutTileDataSuccess(data));
    } catch (error: any) {
      Logger.notify(error);
      dispatch(getWorkoutTileDataFailed(error));
    }
  };
};

export const getAllInstructors = (bookingServiceClient: BookingServiceClient) => {
  return async (dispatch) => {
    try {
      const response: IInstructorResponse = await bookingServiceClient.getAllInstructors();
      if (!isWeb) {
        const avatarsToPreload =
          response.instructors.map((pt) => ({ uri: pt.avatarUrl }));
        FastImage.preload(avatarsToPreload);
      }
      dispatch(getInstructorListSuccess(
        response.instructors,
        undefined, undefined,
      ));
    } catch (error) {
      Logger.notify(error);
    }
  };
};

export const getWeeklyGoalData = (userServiceClient: UserServiceClient) => {
  return async (dispatch) => {
    try {
      dispatch({ type: WEEKLY_GOAL_DATA_REQUEST });
      const response = await userServiceClient.getWeeklyGoalData(
        Date.now(),
        moment.tz.guess(),
      );
      dispatch({ type: WEEKLY_GOAL_DATA_SUCCESS, payload: response });
    } catch (e) {
      dispatch({ type: WEEKLY_GOAL_DATA_FAILURE });
    }
  };
};

const loadInsightsBlogPostsInBackground = (
  dispatch,
  userServiceClient: UserServiceClient,
  longTermGoal,
  foodPreference,
  count: number,
) => {
  loadInsightsBlogPosts(dispatch, userServiceClient, longTermGoal, foodPreference, count)
    .catch((e) => { /* handled in loadInsightsBlogPosts */ });
};

const loadInsightsBlogPosts = async (
  dispatch,
  userServiceClient: UserServiceClient,
  longTermGoal,
  foodPreference,
  count: number,
) => {
  try {
    dispatch({ type: DASHBOARD_BLOG_INSIGHTS_REQUEST });
    const posts = await userServiceClient.getDashboardBlogContent(
      ContentType.Insights,
      longTermGoal,
      foodPreference,
      count,
    );
    dispatch({ type: DASHBOARD_BLOG_INSIGHTS_SUCCESS, payload: posts });
  } catch (e) {
    dispatch({ type: DASHBOARD_BLOG_INSIGHTS_FAILURE });
  }
};

const loadAllInsightsBlogPosts = async (
  dispatch,
  userServiceClient: UserServiceClient,
  longTermGoal,
  foodPreference,
  count: number,
) => {
  try {
    dispatch({
      type: ALL_DASHBOARD_BLOG_INSIGHTS_REQUEST,
      ...analyicsForAction(ALL_DASHBOARD_BLOG_INSIGHTS_REQUEST),
    });
    const posts = await userServiceClient.getDashboardBlogContent(
      ContentType.Insights,
      longTermGoal,
      foodPreference,
      count,
    );
    dispatch({ type: ALL_DASHBOARD_BLOG_INSIGHTS_SUCCESS, payload: posts });
  } catch (e) {
    dispatch({ type: DASHBOARD_BLOG_INSIGHTS_FAILURE });
  }
};

const loadHelpBlogPosts = (
  dispatch,
  userServiceClient: UserServiceClient,
  longTermGoal,
  foodPreference,
  count: number,
) => {
  dispatch(seeAllPressedForHelpBlogs(userServiceClient, longTermGoal, foodPreference));
};

const addSignupSourceWorkoutIdToDeeplinkParams = (dispatch: any, userProfile: UserProfile) => {
  if (userProfile.signupSource?.signupWorkoutInviteId) {
    dispatch(addDeepLinkParams(
      { workoutInviteId: userProfile.signupSource?.signupWorkoutInviteId },
    ));
  }
};

export const dashboardOpened = (
  bookingServiceClient: BookingServiceClient,
  userServiceClient: UserServiceClient,
  waitlistServiceClient: WaitingListServiceClient,
  userProfile: UserProfile) => {
  return async (dispatch, getState: () => IAppStore) => {
    try {
      addSignupSourceWorkoutIdToDeeplinkParams(dispatch, userProfile);
      if (userProfile.signupSource?.signupWorkoutInviteId) {
        await userServiceClient.removeUserSignupWorkoutInvite();
      }
      const count = 3;
      loadInsightsBlogPostsInBackground(
        dispatch, userServiceClient, userProfile.longTermGoal, userProfile.mealPreferences.foodPreference, count);
      loadHelpBlogPosts(
        dispatch, userServiceClient, userProfile.longTermGoal, userProfile.mealPreferences.foodPreference, count);
      dispatch({ type: DASHBOARD_OPENED_REQUEST });

      const { planner } = getState();
      if (planner.fetchingWorkoutSessions) {
        dispatch({ type: DASHBOARD_OPENED_SUCCESS });
      } else {
        const [allWorkoutSessions, waitlistEntries, marketingTiles, influencerBookings] = await Promise.all(
          [
            bookingServiceClient.getMyBookings(
              userProfile.longTermGoal,
            ),
            waitlistServiceClient.getWaitingList(),
            userServiceClient.getMarketingTileData(userProfile.uid),
            bookingServiceClient.getInfluencerBookings(),
          ],
        );
        dispatch({ type: DASHBOARD_OPENED_SUCCESS, payload: {
          allWorkoutSessions, waitlistEntries, marketingTiles, influencerBookings } });
      }
    } catch (error) {
      dispatch({ type: DASHBOARD_OPENED_FAILURE, payload: { error } });
    }
  };
};

export const dashboardBlogCardPressed = (post: IPost) => {
  return (dispatch) => {
    dispatch(closeAllModals());
    dispatch({
      type: DASHBOARD_BLOG_CARD_PRESSED,
      payload: {
        post,
      },
      ...analyicsForAction(DASHBOARD_BLOG_CARD_PRESSED, { blog: post.slug }),
    });
  };
};

export const seeAllPressedForInsights = (
  userServiceClient: UserServiceClient,
  userProfile: UserProfile) => {
  return async (dispatch) => {
    const count = Number.MAX_SAFE_INTEGER;
    loadAllInsightsBlogPosts(dispatch, userServiceClient, userProfile.longTermGoal,
      userProfile.mealPreferences.foodPreference, count)
      .catch((e) => Logger.notify(e));
  };
};

export const seeAllPressedForHelpBlogs = (
  userServiceClient: UserServiceClient,
  longTermGoal,
  foodPreference,
) => {
  return async (dispatch) => {
    const count = Number.MAX_SAFE_INTEGER;
    try {
      dispatch({
        type: ALL_DASHBOARD_BLOG_HELP_REQUEST,
        ...analyicsForAction(ALL_DASHBOARD_BLOG_HELP_REQUEST),
      });
      const posts = await userServiceClient.getDashboardBlogContent(
        ContentType.Help,
        longTermGoal,
        foodPreference,
        count,
      );
      dispatch({ type: ALL_DASHBOARD_BLOG_HELP_SUCCESS, payload: posts });
    } catch (e) {
      dispatch({ type: ALL_DASHBOARD_BLOG_HELP_FAILURE });
    }
  };
};

export const joinLeaderboard = (
  userService: UserServiceClient,
  leaderboard: IUserLeaderboardViewModel,
  joinedFromDashboard: boolean,
  team?: string,
) => {
  return async (dispatch) => {
    if (leaderboard.teams && !team) {
      dispatch(showTeamSelectScreen());
      return;
    }

    dispatch(joinLeaderboardRequest());
    try {
      await userService.joinLeaderboard(
        leaderboard.leaderboardId,
        team,
      );

      const joinedLeaderboard = await userService
        .getLeaderboardInstance(leaderboard.leaderboardId, leaderboard.startDate, leaderboard.endDate)

      if (joinedFromDashboard) {
        await dispatch(getWeeklyGoalData(userService));
      } else {
        await dispatch(loadLeaderboardsForUser(userService));
      }

      dispatch(joinLeaderboardSuccess(joinedLeaderboard));
    } catch (error) {
      dispatch(joinLeaderboardFailure(error));
    }
  };
};

export interface IDashboardOpenedSuccess {
  type: typeof DASHBOARD_OPENED_SUCCESS;
  payload: {
    allWorkoutSessions: UserWorkoutSession[];
  };
}

export const openLeaderboardModal = (
  leaderboard: IUserLeaderboardViewModel,
  didOpenLeaderboardModalFromDashboard: boolean,
  shouldShowSelectTeamScreen: boolean,
) => {
  return (dispatch) => {
    dispatch(closeAllModals());
    dispatch({
      type: OPEN_LEADERBOARD_MODAL,
      payload: {
        selectedLeaderboard: leaderboard,
        didOpenLeaderboardModalFromDashboard,
        shouldShowSelectTeamScreen,
      },
    });
  };
};

export const closeLeaderboardModal = () => {
  return {
    type: CLOSE_LEADERBOARD_MODAL,
  };
};

export const selectTeamAction = (team: string) => {
  return {
    type: SELECT_TEAM,
    payload: team,
  };
};

export const joinLeaderboardRequest = () => {
  return {
    type: JOIN_LEADERBOARD_REQUEST,
  };
};

export const showTeamSelectScreen = () => {
  return {
    type: SHOW_TEAM_SELECT_SCREEN,
  };
};

export const seeAllLeaderboards = () => {
  return async (dispatch) => {
    dispatch(closeLeaderboardModal());
  };
};

export const joinLeaderboardSuccess = (updatedLeaderboard: IUserLeaderboardViewModel) => {
  return {
    type: JOIN_LEADERBOARD_SUCCESS,
    payload: {
      updatedLeaderboard,
    },
  };
};

export const joinLeaderboardFailure = (error: any) => {
  return {
    type: JOIN_LEADERBOARD_FAILURE,
    payload: error,
  };
};
